// Region wall-clock helpers for the Match Nights admin screens.
//
// scheduledAt is always stored and transmitted as UTC. Admins pick and see
// times in the night's regional timezone (CET for EU, ET for NA). There's
// no date-tz library in this app, so wall-time <-> UTC conversion uses the
// standard double-Intl-format trick below.

import { getRegionTimeZone } from "./matchNightOptions";

function dateTimeParts(date, timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(date);
  const map = {};
  parts.forEach((part) => {
    if (part.type !== "literal") map[part.type] = part.value;
  });
  return map;
}

// Formats a UTC Date as the "YYYY-MM-DDTHH:mm" wall-clock string an
// <input type="datetime-local"> expects, using the region's local time.
export function utcDateToRegionInputValue(date, region) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const { timeZone } = getRegionTimeZone(region);
  const map = dateTimeParts(date, timeZone);
  return `${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}`;
}

// Converts a "YYYY-MM-DDTHH:mm" wall-clock string, interpreted in the
// region's timezone, into the corresponding UTC Date instant.
export function regionInputValueToUtcDate(inputValue, region) {
  if (!inputValue) return null;

  // Treat the input digits as if they were UTC to get a reference instant.
  const asIfUtc = new Date(`${inputValue}:00Z`);
  if (Number.isNaN(asIfUtc.getTime())) return null;

  const { timeZone } = getRegionTimeZone(region);
  const map = dateTimeParts(asIfUtc, timeZone);
  const regionWallAsUtc = new Date(
    `${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}:${map.second}Z`
  );

  // The gap between the reference instant and what the region's clock showed
  // at that instant is (the negative of) the current UTC offset. Applying it
  // once more lands on the actual UTC instant for the intended wall time.
  const diffMs = asIfUtc.getTime() - regionWallAsUtc.getTime();
  return new Date(asIfUtc.getTime() + diffMs);
}

export function formatRegionLabel(date, region) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const { timeZone, abbreviation } = getRegionTimeZone(region);
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${formatter.format(date)} ${abbreviation}`;
}
